export const COMMON_APP_NAME = 'common';
export const FLEET_APP_NAME = 'fleet';

export const VEHICLE_ENTITY_ID = 0;
export const CUSTOMER_ENTITY_ID = 1;
export const AGREEMENT_ENTITY_ID = 2;

export const VEHICLE_ENTITY_NAME = 'vehicles';
export const CUSTOMER_ENTITY_NAME = 'customers';
export const AGREEMENT_ENTITY_NAME = 'agreements';

export const ENTITY_IDS_BY_NAME = {
  [VEHICLE_ENTITY_NAME]: VEHICLE_ENTITY_ID,
  [CUSTOMER_ENTITY_NAME]: CUSTOMER_ENTITY_ID,
  [AGREEMENT_ENTITY_NAME]: AGREEMENT_ENTITY_ID,
};

export const ENTITY_NAMES_BY_ID = {
  [VEHICLE_ENTITY_ID]: VEHICLE_ENTITY_NAME,
  [CUSTOMER_ENTITY_ID]: CUSTOMER_ENTITY_NAME,
  [AGREEMENT_ENTITY_ID]: AGREEMENT_ENTITY_NAME,
};

export const ENTITY_PARENT_APPS = {
  [VEHICLE_ENTITY_NAME]: FLEET_APP_NAME,
  [CUSTOMER_ENTITY_NAME]: COMMON_APP_NAME,
  [AGREEMENT_ENTITY_NAME]: FLEET_APP_NAME,
};

export enum FLASH_MESSAGE_TYPES {
  success,
  error,
  warning,
  info,
}

export const INITIAL_FLASH_MESSAGE: {
  shown: boolean;
  type: FLASH_MESSAGE_TYPES;
  translationId: string;
  translationValueObject: Record<string, string | number>;
} = {
  shown: false,
  type: FLASH_MESSAGE_TYPES.success,
  translationId: '',
  translationValueObject: {},
};

export const flashMessageFactory = (message: Partial<typeof INITIAL_FLASH_MESSAGE>): typeof INITIAL_FLASH_MESSAGE => ({
  ...INITIAL_FLASH_MESSAGE,
  ...message,
});

export const PRO_PLAN_ROLES = {
  readOnlyPlanner: 2,
};

export const PRO_PLUS_PLAN_ROLES = {
  driver: 3,
};

export const USER_ROLES = {
  user: 0,
  admin: 1,
  signingCustomer: 999, // Undefined in back-end, used by permissions
  ...PRO_PLAN_ROLES,
  ...PRO_PLUS_PLAN_ROLES,
  coordinator: 4,
};

export const USER_ROLES_TRANSLATION = [
  {
    id: USER_ROLES.user,
    translationId: 'views.FleetSettings.UserManagement.Accounts.Roles.User',
  },
  {
    id: USER_ROLES.admin,
    translationId: 'views.FleetSettings.UserManagement.Accounts.Roles.Admin',
  },
  {
    id: USER_ROLES.readOnlyPlanner,
    translationId: 'views.FleetSettings.UserManagement.Accounts.Roles.ReadOnlyPlanner',
  },
  {
    id: USER_ROLES.driver,
    translationId: 'views.FleetSettings.UserManagement.Accounts.Roles.Driver',
  },
  {
    id: USER_ROLES.coordinator,
    translationId: 'views.FleetSettings.UserManagement.Accounts.Roles.Coordinator',
  },
];

export const ACCOUNT_SETTINGS_SIDE_MENU_ITEMS = [
  {
    translationId: 'containers.AccountSettings.sideMenuProfile',
    link: '/account/profile',
  },
  {
    translationId: 'containers.AccountSettings.sideMenuNotifications',
    link: '/account/notifications',
  },
];

export enum FleetSettingsSideMenuItem {
  AGREEMENT = 'agreement',
  USERS = 'users',
  CHARGES = 'charges',
  PAYMENT = 'payment',
  VEHICLES = 'vehicles',
  TELEMATICS = 'telematics',
  GEOFENCING = 'geofencing',
  DEPARTMENTS = 'departments',
}

export enum UserPermission {
  VIEW = 'view',
  READ = 'read',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  INSPECT = 'inspect',
  MODIFY = 'modify',
  ACCESS = 'access',
  MANAGE = 'manage',
}

export enum AppRoute {
  DEFAULT = '/',
  LOGIN = '/login',
  PASSWORD_RESET = '/password-reset',
  PASSWORD_RESET_UID_TOKEN = '/password-reset/:uid/:token',
  DASHBOARD = '/fleet/dashboard',
  PLANNER = '/fleet/planner',
  VEHICLES = '/fleet/vehicles',
  VEHICLE = '/fleet/vehicles/:id',
  CUSTOMERS = '/fleet/customers',
  CUSTOMER = '/fleet/customers/:id',
  AGREEMENTS = '/fleet/agreements',
  AGREEMENT = '/fleet/agreements/:id',
  AGREEMENT_ESIGN = '/fleet/agreements/:id/esign',
  AGREEMENT_CUSTOMER_SIGNATURE = '/fleet/agreements/:id/customer-signature',
  VALET = '/fleet/valet',
  VALET_TRIP = '/fleet/valet/:id',
  SETTINGS = '/fleet/settings',
  SETTINGS_USERS = '/fleet/settings/users',
  SETTINGS_CHARGES = '/fleet/settings/charges',
  SETTINGS_PAYMENT = '/fleet/settings/payment',
  SETTINGS_DEPARTMENTS = '/fleet/settings/departments',
  SETTINGS_VEHICLES = '/fleet/settings/vehicles',
  SETTINGS_TELEMATICS = '/fleet/settings/telematics',
  SETTINGS_GEOFENCING = '/fleet/settings/geofencing',
  PUBLIC_CUSTOMER_CHECKIN = '/fleet/public/customer-checkin',
  PUBLIC_CUSTOMER_CHECKIN_WELCOME = '/fleet/public/customer-checkin/welcome',
  PUBLIC_CUSTOMER_CHECKIN_STEP = '/fleet/public/customer-checkin/:step',
}

export enum DotAppRoute {
  LOGIN = 'login',
  PASSWORD_RESET = 'password-reset',
  DASHBOARD = 'dashboard',
  PLANNER = 'planner',
  VEHICLES = 'vehicles',
  CUSTOMERS = 'customers',
  AGREEMENTS = 'agreements',
  VALET = 'valet',
  SETTINGS = 'settings',
  SETTINGS_USERS = 'settings.users',
  SETTINGS_CHARGES = 'settings.charges',
  SETTINGS_PAYMENT = 'settings.payment',
  SETTINGS_DEPARTMENTS = 'settings.departments',
  SETTINGS_VEHICLES = 'settings.vehicles',
  SETTINGS_TELEMATICS = 'settings.telematics',
  SETTINGS_GEOFENCING = 'settings.geofencing',
}

export const FLEET_SETTINGS_ACCESS_OPTIONS = {
  view: 'view',
  settings: 'settings',
  field: FleetSettingsSideMenuItem,
};

export const FLEET_SETTINGS_SIDE_MENU_ITEMS = [
  {
    name: FleetSettingsSideMenuItem.AGREEMENT,
    translationId: 'containers.FleetSettings.sideMenuAgreement',
    link: AppRoute.SETTINGS,
  },
  {
    name: FleetSettingsSideMenuItem.USERS,
    translationId: 'containers.FleetSettings.sideMenuUserManagement',
    link: AppRoute.SETTINGS_USERS,
  },
  {
    name: FleetSettingsSideMenuItem.CHARGES,
    translationId: 'containers.FleetSettings.sideMenuCharges',
    link: AppRoute.SETTINGS_CHARGES,
  },
  {
    name: FleetSettingsSideMenuItem.PAYMENT,
    translationId: 'containers.FleetSettings.sideMenuPayment',
    link: AppRoute.SETTINGS_PAYMENT,
  },
  {
    name: FleetSettingsSideMenuItem.DEPARTMENTS,
    translationId: 'containers.FleetSettings.sideMenuDepartments',
    link: AppRoute.SETTINGS_DEPARTMENTS,
  },
  {
    name: FleetSettingsSideMenuItem.VEHICLES,
    translationId: 'containers.FleetSettings.sideMenuVehicles',
    link: AppRoute.SETTINGS_VEHICLES,
  },
  {
    name: FleetSettingsSideMenuItem.TELEMATICS,
    translationId: 'containers.FleetSettings.sideMenuTelematics',
    link: AppRoute.SETTINGS_TELEMATICS,
  },
];

export const FLEET_SETTINGS_GEOFENCING_SIDE_MENU_ITEM = {
  name: FleetSettingsSideMenuItem.GEOFENCING,
  translationId: 'containers.FleetSettings.sideMenuGeofencing',
  link: AppRoute.SETTINGS_GEOFENCING,
};

export const PLATFORM_SETTINGS_SIDE_MENU_ITEMS = [
  {
    translationId: 'containers.PlatformSettings.sideMenuCompanyDetails',
    link: '/settings/company-details',
  },
  {
    translationId: 'containers.PlatformSettings.sideMenuUsersManagement',
    link: [
      {
        translationId: 'containers.PlatformSettings.sideMenuUsers',
        link: '/settings/users',
      },
      {
        translationId: 'containers.PlatformSettings.sideMenuJobTitles',
        link: '/settings/job-titles',
      },
      {
        translationId: 'containers.PlatformSettings.sideMenuDepartments',
        link: '/settings/departments',
      },
    ],
  },
];

export const MANAGED_VIEWS = [
  'Connected',
  'Available',
  'On loan',
  'On hold',
  'Sold',
  'Active Fleet',
  'Open',
  'Closed',
  'Booked',
  'Reserved',
  'Pending Signature',
];

export const expandableNavLink = ['/settings/users', '/settings/job-titles', '/settings/departments'];

export const ODOMETER_STRATEGY = {
  reported: 'reported',
  calculated: 'calculated',
};

export const KEY_CODES = {
  ENTER: 13,
  ESC: 27,
  UP: 38,
  DOWN: 40,
};

export const FEATURE_FLAGS = {};

export const ODOMETER_UNITS = {
  KM: 'km',
  MI: 'mi',
};

export enum Currency {
  CAD = 'cad',
  USD = 'usd',
  EUR = 'eur',
}

export enum URLPrefix {
  FLEETLANE = 'app',
  WEEVE = 'weeve',
  AVIGO = 'avigo',
}
